import { useForm, useStore } from "@tanstack/react-form";
import { useTranslation } from "react-i18next";
import { FormGroup } from "../../../components/FormGroup";
import { FormLabel } from "../../../components/FormLabel";
import { Form } from "../../../components/Form";
import { Input } from "../../../components/Input";
import { MoneyInput } from "../../../components/MoneyInput";
import { FormSection } from "../../../components/FormSection";
import { OfferType } from "@KassaroAB/shared/types/enums/OfferType";
import type { OfferQuantityPrice } from "@KassaroAB/shared/types/offer";
import {
  asDecimal,
  createMoney,
} from "@KassaroAB/shared/utils/money";
import { OfferCommonFields } from "./OfferCommonFields";
import { OfferTargetFields } from "./OfferTargetFields";
import type { OfferTypeFormProps } from "./offerFormTypes";
import { Fields } from "./offerFormStyles";
import {
  buildOfferBase,
  emptyOfferTarget,
  getOfferCommonDefaults,
} from "./offerUtils";
import { useOfferFormCatalog } from "./useOfferFormCatalog";
import { useOfferFormLifecycle } from "./useOfferFormLifecycle";

export const QuantityPriceForm = ({
  formId,
  offer,
  savedTick,
  onSubmit,
  onDirtyChange,
  onTypeChange,
}: OfferTypeFormProps) => {
  const { t } = useTranslation();
  const { currency, productOptions, groupOptions } = useOfferFormCatalog();
  const quantityOffer =
    offer?.type === OfferType.QUANTITY_PRICE
      ? (offer as OfferQuantityPrice)
      : null;
  const isEditing = !!offer;

  const form = useForm({
    defaultValues: {
      ...getOfferCommonDefaults(
        quantityOffer ??
          (offer ? { ...offer, type: OfferType.QUANTITY_PRICE } : undefined),
      ),
      type: OfferType.QUANTITY_PRICE as OfferType,
      target:
        quantityOffer?.target ??
        (offer && "target" in offer ? offer.target : emptyOfferTarget()),
      quantity: quantityOffer ? Math.max(1, quantityOffer.quantity) : 2,
      priceMajor: quantityOffer ? asDecimal(quantityOffer.price) : 0,
      takeawayPriceMajor: quantityOffer?.takeawayPrice
        ? asDecimal(quantityOffer.takeawayPrice)
        : 0,
    },
    onSubmit: ({ value }) => {
      const takeawayMinor = Math.round(Number(value.takeawayPriceMajor) * 100);
      onSubmit({
        ...buildOfferBase(value),
        type: OfferType.QUANTITY_PRICE,
        target: value.target,
        quantity: Math.max(1, Number(value.quantity)),
        price: createMoney(
          Math.round(Number(value.priceMajor) * 100),
          currency,
        ),
        takeawayPrice:
          takeawayMinor > 0 ? createMoney(takeawayMinor, currency) : null,
      });
    },
  });

  const isDirty = useStore(form.store, (state) => !state.isDefaultValue);
  useOfferFormLifecycle({ form, savedTick, onDirtyChange, isDirty });

  return (
    <Form
      id={formId}
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
    >
      <OfferCommonFields
        form={form}
        isEditing={isEditing}
        onTypeChange={onTypeChange}
      />
      <FormSection title={t("offerForm.additionalSettings")}>
        <Fields>
          <form.Field name="target">
            {(field) => (
              <OfferTargetFields
                value={field.state.value}
                onChange={field.handleChange}
                products={productOptions}
                groups={groupOptions}
                productsLabel={t("offerForm.products")}
                groupsLabel={t("offerForm.productGroups")}
              />
            )}
          </form.Field>
          <form.Field name="quantity">
            {(field) => (
              <FormGroup>
                <FormLabel>{t("offerForm.quantity")}</FormLabel>
                <Input
                  type="number"
                  min={1}
                  max={99}
                  value={field.state.value}
                  onChange={(e) =>
                    field.handleChange(
                      Math.max(1, Number(e.target.value) || 1),
                    )
                  }
                  onBlur={field.handleBlur}
                  required
                />
              </FormGroup>
            )}
          </form.Field>
          <form.Field name="priceMajor">
            {(field) => (
              <MoneyInput
                label={t("offerForm.price")}
                amount={field.state.value}
                currency={currency}
                onAmountChange={field.handleChange}
                onAmountBlur={field.handleBlur}
                required
              />
            )}
          </form.Field>
          <form.Field name="takeawayPriceMajor">
            {(field) => (
              <MoneyInput
                label={t("offerForm.takeawayPrice")}
                amount={field.state.value}
                currency={currency}
                onAmountChange={field.handleChange}
                onAmountBlur={field.handleBlur}
              />
            )}
          </form.Field>
        </Fields>
      </FormSection>
    </Form>
  );
};
